import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPipelineSummary } from '@/actions/pipeline';
import type { RootState } from '@/reducers';
import type { PipelineSummary } from '@/reducers/pipelineReducer';

// How often to refresh the summary while mounted.
const REFRESH_MS = 60000;
// Absorbs duplicate mounts (e.g. StrictMode) without letting a remount
// storm turn into extra traffic.
const MIN_FETCH_GAP_MS = REFRESH_MS - 5000;
let lastFetchedAt = 0;

/**
 * Owns the pipeline-summary polling loop. Mount exactly once (in AppSidebar);
 * components that need the data should use usePipelineSummary instead.
 */
export function usePipelineSummaryPoller(): void {
	const dispatch = useDispatch();

	useEffect(() => {
		const maybeFetch = () => {
			// Hidden tabs don't poll; the visibilitychange listener refreshes
			// the badge as soon as the tab is foregrounded again.
			if (document.hidden) return;
			const now = Date.now();
			if (now - lastFetchedAt > MIN_FETCH_GAP_MS) {
				lastFetchedAt = now;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				dispatch(fetchPipelineSummary() as any);
			}
		};

		maybeFetch();
		const intervalId = setInterval(maybeFetch, REFRESH_MS);
		document.addEventListener('visibilitychange', maybeFetch);
		return () => {
			clearInterval(intervalId);
			document.removeEventListener('visibilitychange', maybeFetch);
		};
	}, [dispatch]);
}

/**
 * Read-only access to the current user's pipeline summary (assigned-active
 * count and unread indicators). Does not fetch; polling is owned by
 * usePipelineSummaryPoller.
 */
export function usePipelineSummary(): PipelineSummary {
	return useSelector((state: RootState) => state.pipeline.summary);
}

export default usePipelineSummary;
